import { deriveCostRecipe, materialExtendedPrice, type CostRecipe } from "./cost-recipe";
import { manualRecipePrice } from "./dimension-pricing";
import { materialProposalBasis, materialsApproved, recipeProcurementSafe, scopeNeedsNoPurchaseMaterials, validMaterialAllowance, validMaterialReview } from "./material-allowance";

export interface MaterialsLine {
  name: string;
  category: string;
  quantity: number | string;
  unit: string;
  unitCost: number | string;
  isOptional?: boolean | null;
  quoteCostRecipe?: unknown;
  description?: string | null;
  materialCost?: number | string | null;
  quantityDerivation?: unknown;
  materialAllowanceApproval?: unknown;
  costLibraryTaskId?: string | null;
  sourceVariant?: unknown;
}
export interface MaterialsRow {
  key: string;
  materialId: string;
  name: string;
  spec: string;
  unit: string;
  unitPrice: number;
  quantity: number;
  orderQuantity?: number;
  orderTotal?: number;
  roundedQuantity: number;
  roundPacks: boolean;
  packSize?: number;
  total: number;
  exactTotal: number;
  category: string;
  contributors: { index: number; name: string }[];
  editable: boolean;
  estimated?: boolean;
}
const cents = (n: number) => Math.round(n * 100);
const money = (n: number) => Math.round(n) / 100;
const q4 = (n: number) => Math.round(n * 10000) / 10000;

/** Same penny-rounded extended rows in both the line breakdown and merchant list. */
export function lineMaterialRows(line: MaterialsLine, index: number): MaterialsRow[] {
  const approval = validMaterialAllowance(line);
  if (approval || !line.quoteCostRecipe || !recipeProcurementSafe(line)) {
    return (approval?.materials ?? []).map(material => {
      const key = JSON.stringify(["approved-material", material.name.toLowerCase().trim(),
        material.unit.toLowerCase().trim(), material.unitPrice, material.specification]);
      const total = Number(materialExtendedPrice(material.quantity, material.unitPrice));
      return { key, materialId: key, name: material.name,
        spec: [material.specification, approval?.source === "library"
          ? "Cost Library buy price; confirm before ordering" : "Estimated buy price; confirm before ordering"].filter(Boolean).join(" · "),
        unit: material.unit, unitPrice: material.unitPrice, quantity: material.quantity, roundedQuantity: material.quantity,
        roundPacks: false, total, exactTotal: total, category: line.category, contributors: [{ index, name: line.name }], editable: false, estimated: approval?.source !== "library" };
    });
  }
  const { recipe } = deriveCostRecipe(line.quoteCostRecipe);
  const quantity = Number(line.quantity);
  if (!Number.isFinite(quantity) || quantity < 0) throw new RangeError("Invalid work item quantity");
  const rows = recipe.materials.map(m => ({
    materialId: m.material_id, name: m.name, spec: [m.spec, m.dimension_pricing?.resolved_spec].filter(Boolean).join(" · "), unit: m.unit,
    unitPrice: m.unit_price, quantity: q4(m.quantity_per_unit! * quantity),
    packSize: m.pack_size, roundPacks: m.round_packs ?? false, editable: true,
  }));
  // Specification recipes have a supply allowance, not an invented material ID.
  // Keep that supply visible and quote-local, never conflate it with a catalogue material.
  if (recipe.shape === "specification") rows.unshift({
    materialId: recipe.supply_material_id ?? `supply:${index}`, name: recipe.supply_name ?? `${line.name} — supply`,
    // Steel: short section + Dimensions working, like any other measured material.
    spec: recipe.supply_pricing?.section_rates
      ? [recipe.supply_pricing.resolved_section?.replace(/^(\d+)X(\d+)X([\d.]+)(UB|UC)$/i, "$1 × $2 × $3 $4"),
          recipe.supply_pricing.resolved_spec].filter(Boolean).join(" · ")
      : [recipe.specification, recipe.supply_pricing?.resolved_spec].filter(Boolean).join(" · "),
    unit: line.unit, unitPrice: recipe.supply_allowance, quantity,
    packSize: undefined, roundPacks: false, editable: false,
  });
  return rows.map(row => {
    const exactTotal = Number(materialExtendedPrice(row.quantity, row.unitPrice));
    return {
      ...row, key: JSON.stringify([row.materialId, row.unit, row.unitPrice, row.spec]),
      roundedQuantity: row.packSize ? q4(Math.ceil(row.quantity / row.packSize) * row.packSize) : row.quantity,
      exactTotal, total: exactTotal, category: line.category,
      contributors: [{ index, name: line.name }],
    };
  });
}

export function buildMaterialsList(items: MaterialsLine[], requireApproval = false) {
  const included = new Map<string, MaterialsRow>();
  const optional = new Map<string, MaterialsRow>();
  const missing: { index: number; name: string; subtotal: number; isOptional: boolean }[] = [];
  const pending: { index: number; name: string; isOptional: boolean }[] = [];
  let expected = 0;
  items.forEach((line, index) => {
    if (scopeNeedsNoPurchaseMaterials(line)) return;
    if (!line.quoteCostRecipe || !recipeProcurementSafe(line)) {
      const approved = validMaterialAllowance(line);
      if (!approved) {
        missing.push({ index, name: line.name, subtotal: money(cents(Number(line.quantity) * Number(line.unitCost))), isOptional: !!line.isOptional });
        return;
      }
    }
    if (!materialsApproved(line, requireApproval)) {
      pending.push({ index, name: line.name, isOptional: !!line.isOptional });
      return;
    }
    const rows = lineMaterialRows(line, index);
    const target = line.isOptional ? optional : included;
    for (const row of rows) {
      if (!line.isOptional) expected += cents(row.exactTotal);
      const existing = target.get(row.key);
      if (!existing) target.set(row.key, { ...row });
      else {
        existing.quantity = q4(existing.quantity + row.quantity);
        existing.exactTotal = money(cents(existing.exactTotal) + cents(row.exactTotal));
        existing.contributors.push(...row.contributors);
        // Conflicting pack definitions cannot safely be combined. Keep the exact
        // quantity and explain through the absence of a rounding control.
        if (existing.packSize !== row.packSize) existing.packSize = undefined;
        existing.roundPacks = existing.roundPacks && row.roundPacks;
      }
    }
  });
  const finish = (map: Map<string, MaterialsRow>) => [...map.values()].map(row => {
    const overrides = row.contributors.map(c => validMaterialReview(items[c.index])?.orderQuantities?.[row.key])
      .filter(value => value && q4(value.requiredQuantity) === row.quantity);
    const orderQuantity = overrides.length && overrides.every(value => value!.quantity === overrides[0]!.quantity)
      ? overrides[0]!.quantity : undefined;
    const purchaseQuantity = orderQuantity ?? row.quantity;
    const orderTotal = orderQuantity === undefined ? row.exactTotal : Number(materialExtendedPrice(purchaseQuantity, row.unitPrice));
    const roundedQuantity = row.packSize ? q4(Math.ceil(purchaseQuantity / row.packSize) * row.packSize) : purchaseQuantity;
    const total = row.roundPacks && row.packSize
      ? Number(materialExtendedPrice(roundedQuantity, row.unitPrice)) : orderTotal;
    return { ...row, orderQuantity, orderTotal, roundedQuantity, total };
  }).sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
  const includedRows = finish(included), optionalRows = finish(optional);
  const sum = (rows: MaterialsRow[], field: "total" | "exactTotal") => money(rows.reduce((n, row) => n + cents(row[field]), 0));
  const exactSubtotal = sum(includedRows, "exactTotal");
  if (cents(exactSubtotal) !== expected) throw new Error("Materials subtotal does not match line breakdowns");
  const subtotal = sum(includedRows, "total"), optionalSubtotal = sum(optionalRows, "total");
  return {
    included: includedRows, optional: optionalRows, missing, pending, exactSubtotal, subtotal, optionalSubtotal,
    orderAdjustment: money(includedRows.reduce((n, row) => n + cents(row.orderTotal! - row.exactTotal), 0)),
    optionalOrderAdjustment: money(optionalRows.reduce((n, row) => n + cents(row.orderTotal! - row.exactTotal), 0)),
    roundingExtra: money(includedRows.reduce((n, row) => n + cents(row.total - row.orderTotal!), 0)),
    optionalRoundingExtra: money(optionalRows.reduce((n, row) => n + cents(row.total - row.orderTotal!), 0)),
  };
}
export type MaterialsListData = ReturnType<typeof buildMaterialsList>;

/** Whole grouped purchasing quantity only; never changes measured work or selling recipes. */
export function setMaterialOrderQuantity<T extends MaterialsLine>(items: T[], row: MaterialsRow, quantity: number | null): T[] {
  if (quantity !== null && (!Number.isFinite(quantity) || quantity < 0 || quantity > 1_000_000 || Math.abs(quantity - q4(quantity)) > 1e-8))
    throw new RangeError("Enter an order quantity from 0 to 1,000,000 with at most four decimal places");
  const current = buildMaterialsList(items);
  const group = row.contributors[0] && items[row.contributors[0].index]?.isOptional ? current.optional : current.included;
  const actual = group.find(candidate => candidate.key === row.key);
  if (!actual) throw new Error("This material has changed. Reopen the Materials List and try again.");
  const contributors = new Set(actual.contributors.map(c => c.index));
  return items.map((line, index) => {
    if (!contributors.has(index)) return line;
    const review = validMaterialReview(line);
    const orderQuantities = { ...review?.orderQuantities };
    if (quantity === null || quantity === actual.quantity) delete orderQuantities[row.key];
    else orderQuantities[row.key] = { quantity, requiredQuantity: actual.quantity };
    if (!review && !Object.keys(orderQuantities).length) return line;
    return { ...line, materialAllowanceApproval: {
      ...(review ?? { version: 1 as const, mode: "automatic" as const, source: "builder" as const,
        recipeApproval: true, approved: true, basis: materialProposalBasis(line),
        amount: money(lineMaterialRows(line, index).reduce((sum, material) => sum + cents(material.exactTotal), 0)) }),
      orderQuantities,
    } };
  });
}

/** All matching IDs, including optional lines and previously different prices. */
export function updateMaterialPrice<T extends MaterialsLine>(items: T[], materialId: string, price: number) {
  if (!Number.isFinite(price) || price < 0 || price > 99_999_999.99 || Math.abs(price * 100 - Math.round(price * 100)) > 0.00001)
    throw new RangeError("Enter a non-negative price with at most two decimal places");
  let affected = 0;
  const lines = items.map(line => {
    if (!line.quoteCostRecipe) return line;
    const { recipe } = deriveCostRecipe(line.quoteCostRecipe);
    if (!recipe.materials.some(m => m.material_id === materialId)) return line;
    affected++;
    const derived = deriveCostRecipe(manualRecipePrice({ ...recipe, status: "user_edited", materials: recipe.materials.map(m =>
      m.material_id === materialId ? { ...m, unit_price: price } : m) }));
    const quantity = Number(line.quantity);
    const materialCost = lineMaterialRows({ ...line, quoteCostRecipe: derived.recipe }, 0).reduce((n, r) => n + cents(r.exactTotal), 0) / 100;
    return {
      ...line, quoteCostRecipe: derived.recipe, unitCost: Number(derived.rateTypical),
      labourCost: money(cents(derived.recipe.labour_allowance * quantity)),
      materialCost, plantCost: money(cents(derived.recipe.plant_allowance * quantity)),
    };
  });
  return { lines, affected };
}

export function setMaterialPackRounding<T extends MaterialsLine>(items: T[], row: MaterialsRow, enabled: boolean): T[] {
  const contributing = new Set(row.contributors.map(c => c.index));
  return items.map((line, index) => {
    if (!contributing.has(index) || !line.quoteCostRecipe) return line;
    const { recipe } = deriveCostRecipe(line.quoteCostRecipe);
    const quoteCostRecipe: CostRecipe = { ...recipe, materials: recipe.materials.map(m =>
      m.material_id === row.materialId && m.unit === row.unit && m.unit_price === row.unitPrice
        ? { ...m, round_packs: enabled } : m) };
    const next = { ...line, quoteCostRecipe };
    const review = validMaterialReview(line);
    return review ? { ...next, materialAllowanceApproval: { ...review, basis: materialProposalBasis(next) } } : next;
  });
}

/** No client/address/description fields are accepted or read by this export. */
export function materialsCsv(data: MaterialsListData): string {
  const cell = (value: unknown) => {
    const text = String(value ?? "");
    const safe = /^[\s\uFEFF]*[=+\-@]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const out: unknown[][] = [["Material", "Spec", "Quantity", "Unit", "Unit price", "Total", "Used in", "Group"]];
  const add = (rows: MaterialsRow[], group: string, total: number) => {
    if (!rows.length) return;
    rows.forEach(row => out.push([
      row.name, `${row.spec}${row.roundPacks && row.packSize ? " (rounded to packs)" : ""}`.trim(),
      (row.roundPacks && row.packSize ? row.roundedQuantity : row.orderQuantity ?? row.quantity).toFixed(row.orderQuantity === undefined ? 2 : 4),
      row.unit, row.unitPrice.toFixed(2), row.total.toFixed(2),
      row.contributors.map(c => c.name).join(", "), group,
    ]));
    out.push([`${group} total`, "", "", "", "", total.toFixed(2), "", group]);
  };
  add(data.included, "Included", data.subtotal);
  add(data.optional, "Optional", data.optionalSubtotal);
  // A supplier order must contain purchasable materials, never work items or budget estimates.
  return "\uFEFF" + out.map(row => row.map(cell).join(",")).join("\r\n") + "\r\n";
}
